import { createSelector } from '@reduxjs/toolkit'
import { TRootState } from 'store/types'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import { AIVideoSocketMessageStatus, IFastTrackConfig } from 'types/aiVideo'
import { MediaStatus } from 'types/media'
import {
  getDefaultAIVideoModel,
  getActiveReferenceItems,
  toApiReferenceMedias,
  evaluateFirstAndLastFrameRule
} from './utils'
import { ReferenceMediaImageType } from 'types/aiVideo'
import { AI_VIDEO_MODEL_CODES } from 'consts'
import { getStringifiedQuality } from './utils'
import { MIN_PREMIUM_QUALITY } from 'consts'
import { isImagePath } from 'utils/media'
import { isBlobUrl } from 'utils'
import { projectSelectors } from '../project/selectors'
import { aiImageSelectors } from '../aiImage/selectors'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'

const selector = (state: TRootState) => state.aiVideo

const isRequestingGeneration = createSelector(
  selector,
  state => state.isRequestingGeneration
)

const isPowerModeRequiredPopupOpen = createSelector(
  selector,
  state => state.isPowerModeRequiredPopupOpen
)

const referenceMedias = createSelector(
  selector,
  state => state.generationParams.referenceMedias
)

const referenceMode = createSelector(selector, state => state.referenceMode)

const framesBucket = createSelector(
  [referenceMedias],
  medias => medias?.frames ?? []
)

const multiRefImagesBucket = createSelector(
  [referenceMedias],
  medias => medias?.images ?? []
)

const activeReferenceItems = createSelector(
  [referenceMedias, referenceMode],
  (medias, mode) => getActiveReferenceItems(medias, mode)
)

const firstFrame = createSelector([framesBucket], frames =>
  frames.find(frame => frame.type === ReferenceMediaImageType.FIRST_FRAME)
)

const lastFrame = createSelector([framesBucket], frames =>
  frames.find(frame => frame.type === ReferenceMediaImageType.LAST_FRAME)
)

const firstFrameUrl = createSelector([firstFrame], frame => frame?.url)
const lastFrameUrl = createSelector([lastFrame], frame => frame?.url)

const hasUploadedImage = createSelector([firstFrameUrl], url => !!url)
const hasLastFrame = createSelector([lastFrameUrl], url => !!url)

const firstFrameNativeRatio = createSelector(
  [firstFrame],
  frame => frame?.nativeRatio ?? null
)

const lastFrameNativeRatio = createSelector(
  [lastFrame],
  frame => frame?.nativeRatio ?? null
)

const isMultiRefMode = createSelector(
  [referenceMode],
  mode => mode === ReferenceMode.IMAGES
)

const isAnyRefImageUploading = createSelector([activeReferenceItems], items =>
  items.some(item => item.isLoading || isBlobUrl(item.url))
)

// converts refs to {url,type} format for api request
const apiReferenceMedias = createSelector([activeReferenceItems], items =>
  toApiReferenceMedias(items)
)

const hasRefItemsInActiveBucket = createSelector(
  [activeReferenceItems],
  items => !!items?.length
)

const hasFrameSelected = createSelector(
  [framesBucket, referenceMode],
  (frames, mode) => mode === ReferenceMode.FRAMES && frames.length > 0
)

const hasUploadedAnyReferenceMedia = createSelector(
  [referenceMedias],
  medias => !!medias?.images?.length || !!medias?.frames?.length
)

const generationParams = createSelector(
  selector,
  state => state.generationParams
)

const isGeneration = createSelector(
  [generationParams, projectDataSelectors.mediaItems],
  (generationParams, mediaItems) => {
    const targetMediaItemId = generationParams?.targetMediaItemId
    if (!targetMediaItemId) return true

    if (generationParams?.parentProjectId) return false

    const targetItemPath = mediaItems.find(
      item => item.id === targetMediaItemId
    )?.meta?.path
    const isImageAnimation = !!targetItemPath && isImagePath(targetItemPath)

    return isImageAnimation
  }
)

const isAnimate = createSelector(
  [generationParams, hasRefItemsInActiveBucket],
  (generationParams, hasRefItemsInActiveBucket) =>
    !!generationParams.aiImageId || hasRefItemsInActiveBucket
)

const aiVideoModels = createSelector(selector, state => state.aiVideoModels)

const hasVideoModels = createSelector(
  aiVideoModels,
  aiVideoModels => aiVideoModels.length > 0
)

const selectedModelCode = createSelector(
  selector,
  state => state.generationParams.selectedModel
)

const isConsistencyEnabled = createSelector(
  selector,
  state => state.generationParams.isConsistencyEnabled
)

const smartPrompt = createSelector(
  selector,
  state => state.generationParams.smartPrompt
)

const selectedModel = createSelector(
  [selectedModelCode, aiVideoModels],
  (modelCode, aiVideoModels) => {
    const specificModel = aiVideoModels?.find(
      model => model.model === modelCode
    )
    if (specificModel) {
      return specificModel
    }

    return getDefaultAIVideoModel(aiVideoModels)
  }
)

const duration = createSelector(
  selector,
  state => state.generationParams.duration
)

const aspectRatio = createSelector(
  selector,
  state => state.generationParams.aspectRatio
)
const quality = createSelector(
  selector,
  state => state.generationParams.quality
)

const isFirstAndLastFrameSupported = createSelector(
  [selectedModel, duration, quality, aspectRatio],
  (model, duration, quality, aspectRatio) => {
    const support = model?.capabilities.firstAndLastFrameSupport
    if (!support?.isSupported) return false
    if (!support.conditions) return true
    return evaluateFirstAndLastFrameRule(support.conditions, {
      duration,
      quality,
      aspectRatio
    })
  }
)

const multipleReferenceMediasCapability = createSelector(
  [selectedModel],
  model => model?.capabilities.multipleReferenceMedias
)

const fastTrackConfig = createSelector(
  [selectedModel],
  (model): IFastTrackConfig =>
    model?.capabilities.fastTrackConfig ?? { isSupported: false }
)

const isFastTrackSupported = createSelector(
  [fastTrackConfig],
  config => config.isSupported
)

const isFastTrack = createSelector(
  selector,
  state => state.generationParams.isFastTrack
)

// Power mode is a paid feature, so free users are never "active" even if the
// redux flag is true (e.g. the default). Single truth for: toggle display,
// credits multiplier, popup gate, request body.
const isFastTrackActive = createSelector(
  [isFastTrackSupported, isFastTrack, userSelectors.isFreeUser],
  (isSupported, isEnabled, isFree) => isSupported && isEnabled && !isFree
)

const videoCreditsMultiplier = createSelector(
  [fastTrackConfig, isFastTrackActive],
  (config, isActive) => (isActive ? (config.multiplier ?? 1) : 1)
)

const perUserConcurrency = createSelector(
  [selectedModel],
  model => model?.capabilities.perUserConcurrency
)

const supportedImageMimeTypes = createSelector(
  [selectedModel],
  model => model?.capabilities.supportedMediaMimeTypes?.images
)

const isMultiRefSupported = createSelector(
  [multipleReferenceMediasCapability],
  cap => cap?.isSupported ?? false
)

const multiRefMaxImageCount = createSelector(
  [multipleReferenceMediasCapability],
  cap => cap?.images?.maxCount
)

const aiImageId = createSelector(
  selector,
  state => state.generationParams.aiImageId
)

const prompt = createSelector(selector, state => state.generationParams.prompt)

const targetMediaItemId = createSelector(
  selector,
  state => state.generationParams.targetMediaItemId
)

const isGenerating = createSelector(
  [projectDataSelectors.mediaItems],
  mediaItems => mediaItems.some(item => checkIfAIVideoIsGenerating(item))
)

const hasLastFrameWithoutFirstFrame = createSelector(
  [lastFrameUrl, firstFrameUrl, isFirstAndLastFrameSupported],
  (lastFrame, firstFrame, isSupported) =>
    isSupported && Boolean(lastFrame) && !firstFrame
)

const isGenerateButtonActive = createSelector(
  [
    selectedModel,
    prompt,
    isAnyRefImageUploading,
    isRequestingGeneration,
    hasRefItemsInActiveBucket,
    hasLastFrameWithoutFirstFrame
  ],
  (
    model,
    promptText,
    isAnyRefItemUploading,
    isRequestingGeneration,
    hasImage,
    hasOrphanedLastFrame
  ) => {
    if (
      !model ||
      isAnyRefItemUploading ||
      isRequestingGeneration ||
      hasOrphanedLastFrame
    ) {
      return false
    }

    const hasPrompt = promptText?.length >= 1
    return hasImage || hasPrompt
  }
)

const historyList = createSelector(
  selector,
  state => state.aiVideoHistory.videos
)

const isHistoryListLoading = createSelector(
  selector,
  state => state.aiVideoHistory.isLoading
)

const isHistoryInitiallyLoaded = createSelector(
  selector,
  state => state.aiVideoHistory.isDataInitiallyLoaded
)

const isLoadingTabData = createSelector(
  selector,
  state => state.isLoadingTabData
)

const availableDurationOptions = createSelector([selectedModel], model => {
  if (!model?.options) return []

  return model.options.map(option => ({
    value: option.duration,
    label: `${option.duration}s`
  }))
})

const availableAspectRatioOptions = createSelector([selectedModel], model => {
  if (!model?.capabilities.aspectRatios) return []

  return model.capabilities.aspectRatios.map(ratio => ({
    value: ratio,
    label: ratio
  }))
})

const selectedDurationOption = createSelector(
  [availableDurationOptions, duration],
  (options, selectedDuration) => {
    return (
      options.find(option => option.value === selectedDuration) || options[0]
    )
  }
)

const hasAspectRatioOptions = createSelector(
  availableAspectRatioOptions,
  options => options.length > 0
)

/**
 * For each available aspect ratio, computes how many frames need resizing.
 * resizeFrameCount === 0 means all frames natively match that ratio.
 */
const resizeCountByRatio = createSelector(
  [
    availableAspectRatioOptions,
    firstFrameNativeRatio,
    lastFrameNativeRatio,
    hasUploadedImage,
    hasLastFrame,
    selectedModel
  ],
  (options, firstNative, lastNative, hasFirst, hasLast, model) => {
    const noResizeNeeded = model?.capabilities.isMediaResizingSupported === true
    const totalFramesCount = (hasFirst ? 1 : 0) + (hasLast ? 1 : 0)

    return options.reduce<Record<string, { resizeFrameCount: number }>>(
      (acc, option) => {
        const ratio = String(option.value)

        if (noResizeNeeded) {
          acc[ratio] = { resizeFrameCount: 0 }
          return acc
        }

        const firstMatches = hasFirst && firstNative === ratio
        const lastMatches = hasLast && lastNative === ratio
        const matchingFramesCount =
          (firstMatches ? 1 : 0) + (lastMatches ? 1 : 0)

        acc[ratio] = {
          resizeFrameCount: totalFramesCount - matchingFramesCount
        }
        return acc
      },
      {}
    )
  }
)

const isSelectedVideoModelUnlimited = createSelector(
  [selectedModel, quality, userSelectors.unlimitedAiVideoModels],
  (model, selectedQuality, unlimitedVideoModels) => {
    if (!model || !unlimitedVideoModels?.length) {
      return false
    }

    const unlimitedModel = unlimitedVideoModels.find(
      m => m.aiVideoModelId === model.id
    )

    if (!unlimitedModel) {
      return false
    }

    return unlimitedModel.qualities.some(q => q.value === selectedQuality)
  }
)

// Power mode bypasses the unlimited zero-out: when fast-track is active the
// user pays base × multiplier even on an otherwise-unlimited model, since the
// fast lane is billed regardless of plan.
const videoGenerationCost = createSelector(
  [
    selector,
    isSelectedVideoModelUnlimited,
    isFastTrackActive,
    videoCreditsMultiplier
  ],
  (state, isUnlimited, isFastActive, multiplier) => {
    if (isUnlimited && !isFastActive) return 0
    // Backend uses Math.ceil when applying the multiplier — match it so the
    // FE display agrees with what gets billed.
    return Math.ceil(state.videoGenerationCost * multiplier)
  }
)

// This needed in power mode popup to show the cost before the toggle is flipped can't be done in the selector because it's not yet calculated.
const videoGenerationCostIfPowerOn = createSelector(
  [selector, fastTrackConfig],
  (state, config) =>
    Math.ceil(state.videoGenerationCost * (config.multiplier ?? 1))
)

const activeTab = createSelector(selector, state => state.activeTab)

const aiVideoHistoryTotalCount = createSelector(
  selector,
  state => state.aiVideoHistory.totalCount
)

const aiVideoHistoryRawOffset = createSelector(
  selector,
  state => state.aiVideoHistory.rawOffset
)

const isHistoryScopedToCurrentProject = createSelector(
  selector,
  state => state.aiVideoHistory.isScopedToCurrentProject
)

const aiVideoOverlayData = createSelector(
  [generationParams, projectDataSelectors.mediaItems],
  (generationParams, mediaItems) => {
    const statusMap = {
      [MediaStatus.PENDING]: AIVideoSocketMessageStatus.PROCESSING,
      [MediaStatus.OK]: AIVideoSocketMessageStatus.COMPLETED,
      [MediaStatus.ERROR]: AIVideoSocketMessageStatus.FAILED
    }

    const mediaItem = mediaItems.find(
      item => item.id === String(generationParams.targetMediaItemId)
    )

    return {
      ...generationParams,
      videoUrl: mediaItem?.meta?.path,
      status: statusMap[mediaItem?.meta?.status as MediaStatus]
    }
  }
)
const availableQualityOptions = createSelector(
  [selectedModel, duration, userSelectors.isFreeUser],
  (model, selectedDuration, isFreeUser) => {
    if (!model?.capabilities.qualities) return []

    const currentDurationQualities = model.options.find(
      option => option.duration === selectedDuration
    )?.qualities

    if (!currentDurationQualities) return []
    return currentDurationQualities.map(item => ({
      value: item.quality,
      label: getStringifiedQuality(item.quality),
      isPremium: isFreeUser && item.quality >= MIN_PREMIUM_QUALITY
    }))
  }
)

const expectedGenerationDuration = createSelector(
  [selectedModel, quality, duration],
  (model, quality, duration) => {
    return model.options
      .find(option => option.duration === duration)
      ?.qualities.find(item => item.quality === quality)
      ?.generationAvgDurationSeconds
  }
)
const refImageSelectedStyleId = createSelector(
  selector,
  state => state.generationParams.refImageSelectedStyleId
)

const uploadedImagePath = createSelector(
  selector,
  state => state.generationParams.uploadedImagePath
)

const isFromDifferentProject = createSelector(
  [selector, projectSelectors.id],
  (state, currentProjectId) => {
    const { sourceProjectId } = state.generationParams
    if (!sourceProjectId) return false

    return sourceProjectId !== Number(currentProjectId)
  }
)

const versionHistoryItems = createSelector(
  selector,
  state => state.versionHistory.items
)

const isVersionHistoryLoading = createSelector(
  selector,
  state => state.versionHistory.isLoading
)

const isVersionHistoryLoaded = createSelector(
  selector,
  state => state.versionHistory.isLoaded
)

const showVersionHistoryTab = createSelector(
  selector,
  state => state.showVersionHistoryTab
)

const renderforestFastModel = createSelector(
  [aiVideoModels],
  models =>
    models?.find(model => model.model === AI_VIDEO_MODEL_CODES.RENDERFOREST) ||
    null
)

const renderforestFastUnlimitedMaxQuality = createSelector(
  [renderforestFastModel, userSelectors.unlimitedAiVideoModels],
  (model, unlimitedVideoModels) => {
    if (!model || !unlimitedVideoModels?.length) return null

    const unlimitedModel = unlimitedVideoModels.find(
      m => m.aiVideoModelId === model.id
    )

    if (!unlimitedModel?.qualities?.length) return null

    return Math.max(...unlimitedModel.qualities.map(q => q.value))
  }
)

const imageProcessingCosts = createSelector(
  [
    hasUploadedImage,
    refImageSelectedStyleId,
    aspectRatio,
    hasLastFrame,
    aiImageSelectors.refImageStyleCreditsCost,
    aiImageSelectors.refImageSizeCreditsCost,
    resizeCountByRatio
  ],
  (hasImage, hasStyle, ratio, hasLast, styleCost, sizeCost, resizeCounts) => {
    const frameCount = hasLast ? 2 : 1
    const hasResize = hasImage && ratio !== null
    const resizeFrameCount =
      hasResize && resizeCounts[ratio]
        ? resizeCounts[ratio].resizeFrameCount
        : hasResize
          ? frameCount
          : 0
    const imageProcessingCosts = {
      restyleCost: 0,
      resizeCost: 0,
      resizeFrameCount
    }
    if (hasStyle) {
      imageProcessingCosts.restyleCost = styleCost * frameCount
    }
    if (resizeFrameCount > 0) {
      imageProcessingCosts.resizeCost = sizeCost * resizeFrameCount
    }
    return imageProcessingCosts
  }
)

export const aiVideoSelectors = {
  activeTab,
  isRequestingGeneration,
  isPowerModeRequiredPopupOpen,
  referenceMedias,
  framesBucket,
  firstFrame,
  lastFrame,
  lastFrameUrl,
  isFirstAndLastFrameSupported,
  isAnyRefImageUploading,
  hasRefItemsInActiveBucket,
  hasFrameSelected,
  referenceMode,
  isMultiRefMode,
  activeReferenceItems,
  multiRefImagesBucket,
  apiReferenceMedias,
  multipleReferenceMediasCapability,
  isMultiRefSupported,
  multiRefMaxImageCount,
  fastTrackConfig,
  isFastTrackSupported,
  isFastTrack,
  isFastTrackActive,
  videoCreditsMultiplier,
  perUserConcurrency,
  videoGenerationCostIfPowerOn,
  generationParams,
  isGeneration,
  isAnimate,
  selectedModelCode,
  selectedModel,
  isConsistencyEnabled,
  smartPrompt,
  duration,
  aspectRatio,
  aiImageId,
  prompt,
  targetMediaItemId,
  isGenerating,
  isGenerateButtonActive,
  availableDurationOptions,
  availableAspectRatioOptions,
  selectedDurationOption,
  hasAspectRatioOptions,
  resizeCountByRatio,
  historyList,
  isHistoryListLoading,
  isHistoryInitiallyLoaded,
  isLoadingTabData,
  aiVideoModels,
  isSelectedVideoModelUnlimited,
  videoGenerationCost,
  aiVideoHistoryTotalCount,
  aiVideoHistoryRawOffset,
  isHistoryScopedToCurrentProject,
  aiVideoOverlayData,
  availableQualityOptions,
  quality,
  expectedGenerationDuration,
  refImageSelectedStyleId,
  isFromDifferentProject,
  uploadedImagePath,
  versionHistoryItems,
  isVersionHistoryLoading,
  isVersionHistoryLoaded,
  hasVideoModels,
  showVersionHistoryTab,
  renderforestFastModel,
  renderforestFastUnlimitedMaxQuality,
  imageProcessingCosts,
  supportedImageMimeTypes,
  hasUploadedImage,
  hasUploadedAnyReferenceMedia
}
